/* global React, Icon, Card, Button, Segmented, KPI, toast, fmt, fmt0 */
// ============================================================
// PLUMME 3 — Privat økonomi · Rapporter
// Måneds- og årsrapport bygget fra importerte transaksjoner
// (readmodel fra getPersonalFinanceOverview). Sammenligningsåret
// hentes fra backend via fetchYear-callbacken.
// Oppsummering, score, observasjoner og utsikt regnes fra tallene.
// ============================================================
const { useState: rptUseState, useEffect: rptUseEffect, useMemo: rptUseMemo } = React;

const RPT_M = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]; // month numbers; names come from t('month.short.' + n)
const rptMonthLong = (index) => t('po.month.' + (index + 1));
const RPT_COLORS = ['var(--plum-700)', 'var(--plum-500)', 'var(--plum-400)', 'var(--plum-600)', 'var(--warning)', 'var(--plum-300)', 'var(--success)', 'var(--muted-2)'];

const rptPct = (now, was) => (was ? Math.round((now - was) / Math.abs(was) * 1000) / 10 : null);
const rptPctStr = (delta) => (delta === null || delta === undefined ? '—' : `${delta > 0 ? '+' : ''}${delta.toLocaleString(PlummeI18n.intlLocale(), { maximumFractionDigits: 1 })} %`);
const rptDec = (value) => Number(value || 0).toLocaleString(PlummeI18n.intlLocale(), { maximumFractionDigits: 1 });
const rptLower = (text) => String(text || '').charAt(0).toLowerCase() + String(text || '').slice(1);
function rptGrade(score) {
  const table = [[90, 'A'], [85, 'A−'], [80, 'B+'], [75, 'B'], [70, 'B−'], [63, 'C+'], [55, 'C'], [45, 'D'], [0, 'E']];
  return (table.find(([limit]) => score >= limit) || table[table.length - 1])[1];
}
const rptVerdict = (score) => (score >= 85 ? t('pkn.rpt.verdict.strong') : score >= 75 ? t('pkn.rpt.verdict.solid') : score >= 65 ? t('pkn.rpt.verdict.fine') : score >= 50 ? t('pkn.rpt.verdict.mixed') : t('pkn.rpt.verdict.demanding'));
const rptScoreColor = (score) => (score >= 80 ? 'var(--success)' : score >= 63 ? 'var(--plum-600)' : score >= 50 ? 'var(--warning)' : 'var(--error)');

const rptTxYear = (transaction) => Number(String(transaction?.date || transaction?.month || '').slice(0, 4));
const rptTxMonth = (transaction) => {
  const month = Number(String(transaction?.date || transaction?.month || '').slice(5, 7));
  return Number.isFinite(month) ? month - 1 : -1;
};
const rptTxType = (transaction) => transaction?.type || (Number(transaction?.amount || 0) >= 0 ? 'income' : 'expense');

// Bygger perioderapport (måned eller år) fra transaksjonene i modellen.
function rptBuildPeriod(model, { monthIndex, monthly, year }) {
  const months = RPT_M.map((_, index) => ({ expenses: 0, income: 0, month: `${year}-${String(index + 1).padStart(2, '0')}` }));
  let summary = { debtPayments: 0, expenses: 0, income: 0, netCashflow: 0, savings: 0, savingsRate: 0, transactions: 0, transfers: 0, creditCardPayments: 0 };
  const categories = new Map();
  const all = Array.isArray(model?.transactions) ? model.transactions : [];
  const inYear = all.filter((transaction) => {
    const txYear = rptTxYear(transaction);
    return !Number.isFinite(txYear) || txYear <= 1900 || txYear === year;
  });
  inYear.forEach((transaction) => {
    const amount = Math.abs(Number(transaction.amount || 0));
    const type = rptTxType(transaction);
    const index = rptTxMonth(transaction);
    if (index >= 0 && months[index]) {
      if (type === 'income') months[index].income += amount;
      else if (type === 'expense') months[index].expenses += amount;
    }
  });
  const selected = monthly ? inYear.filter((transaction) => rptTxMonth(transaction) === monthIndex) : inYear;
  summary = { ...summary, ...window.PoPeriods.summary(selected) };
  selected.forEach((transaction) => {
    const amount = Math.abs(Number(transaction.amount || 0));
    const type = rptTxType(transaction);
    if (type === 'expense') {
      const name = transaction.category || 'Annet';
      const entry = categories.get(name) || { amount: 0, category: name, count: 0, transactions: [] };
      entry.amount += amount;
      entry.count += 1;
      entry.transactions.push(transaction);
      categories.set(name, entry);
    }
  });
  summary.savingsRate = summary.income > 0 ? (summary.savings / summary.income) * 100 : 0;
  return { categories: [...categories.values()].sort((a, b) => b.amount - a.amount), monthly: months, summary };
}

function RptBars({ monthly, rows, selected }) {
  const width = 860;
  const height = 230;
  const pad = { bottom: 28, left: 14, right: 8, top: 16 };
  const innerHeight = height - pad.top - pad.bottom;
  const innerWidth = width - pad.left - pad.right;
  const max = Math.max(1, ...rows.flatMap((row) => [Number(row.income || 0), Number(row.expenses || 0)])) * 1.12;
  const columnWidth = innerWidth / 12;
  const barWidth = 8;
  const y = (value) => pad.top + innerHeight * (1 - value / max);
  const ticks = [0, max * 0.5, max].map((value) => Math.round(value));
  return <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
    {ticks.map((tick, index) => <g key={index}>
      <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} stroke="var(--border)" strokeWidth="1" />
      {index < 2 && <text x={pad.left + 2} y={y(tick) - 5} fontSize="11.5" fontWeight="700" fill="var(--muted-2)">{fmt0(tick)}</text>}
    </g>)}
    {RPT_M.map((label, index) => {
      const cx = pad.left + columnWidth * index + columnWidth / 2;
      const row = rows[index] || {};
      const dim = monthly && selected !== null && selected !== index ? 0.32 : 1;
      return <g key={label} opacity={dim}>
        <rect x={cx - barWidth - 1.5} y={y(Number(row.income || 0))} width={barWidth} height={Math.max(2, y(0) - y(Number(row.income || 0)))} rx="2.5" fill="var(--success)" />
        <rect x={cx + 1.5} y={y(Number(row.expenses || 0))} width={barWidth} height={Math.max(2, y(0) - y(Number(row.expenses || 0)))} rx="2.5" fill="var(--plum-500)" />
        <text x={cx} y={height - 9} fontSize="12" fontWeight={monthly && selected === index ? 900 : 700} fill={monthly && selected === index ? 'var(--plum-700)' : 'var(--muted)'} textAnchor="middle">{t('month.short.' + label)}</text>
      </g>;
    })}
  </svg>;
}

function PoReport({ availableYears = [], fetchYear, firstName = '', model, onAsk, onOpenCategory, year, setYear }) {
  const Sec = window.PoSecHead;
  const [mode, setMode] = rptUseState('mnd');
  const [month, setMonth] = rptUseState(() => Math.max(0, Math.min(11, new Date().getMonth())));
  const [compareYear, setCompareYear] = rptUseState(0);
  const [compareModel, setCompareModel] = rptUseState(null);

  const selectedYear = Number(year) || new Date().getFullYear();
  const monthly = mode === 'mnd';
  const years = rptUseMemo(() => [...new Set([...availableYears, selectedYear])].filter(Boolean).sort((a, b) => a - b), [availableYears, selectedYear]);

  rptUseEffect(() => {
    let alive = true;
    setCompareModel(null);
    if (!compareYear || compareYear === selectedYear || !fetchYear) return () => { alive = false; };
    Promise.resolve(fetchYear(compareYear))
      .then((next) => { if (alive) setCompareModel(next || null); })
      .catch(() => { if (alive) setCompareModel(null); });
    return () => { alive = false; };
  }, [compareYear, fetchYear, selectedYear]);

  const current = rptBuildPeriod(model, { monthIndex: month, monthly, year: selectedYear });
  const chart = rptBuildPeriod(model, { monthIndex: month, monthly: false, year: selectedYear });
  const compareEffective = compareYear && compareYear !== selectedYear && compareModel ? compareYear : null;
  const compare = compareEffective ? rptBuildPeriod(compareModel, { monthIndex: month, monthly, year: compareEffective }) : null;
  const compareByCategory = new Map((compare?.categories || []).map((entry) => [entry.category, entry]));

  const income = current.summary.income;
  const cost = current.summary.expenses;
  const saved = current.summary.netCashflow;
  const savingsRate = income ? current.summary.savings / income * 100 : 0;
  const compareSaved = compare ? compare.summary.netCashflow : null;
  const compareRate = compare && compare.summary.income ? compareSaved / compare.summary.income * 100 : null;
  const hasData = current.summary.transactions > 0;

  const now = new Date();
  const ongoing = monthly && selectedYear === now.getFullYear() && month === now.getMonth();
  const periodDescriptor = window.PoPeriods.describe(ongoing ? window.PoPeriods.MONTH_TO_DATE : (monthly ? month + 1 : window.PoPeriods.YEAR), selectedYear, model?.transactions || [], now);
  const periodLabel = window.PoPeriods.format(periodDescriptor) || (monthly ? `${rptMonthLong(month)} ${selectedYear}` : t('pkn.rpt.wholeYear', { year: selectedYear }));
  const compareLabel = monthly ? `${t('month.short.' + RPT_M[month])}. ${compareEffective}` : String(compareEffective || '');

  const sub = (value, was, isPp) => {
    if (was === null || was === undefined) return null;
    if (isPp) return t('pkn.rpt.ppVs', { delta: `${value - was > 0 ? '+' : ''}${rptDec(value - was)}`, year: compareEffective });
    return t('pkn.rpt.pctVs', { pct: rptPctStr(rptPct(value, was)), year: compareEffective });
  };

  // ---------- SuperPlummas oppsummering ----------
  const aiSummary = rptUseMemo(() => {
    if (!hasData) {
      return {
        bad: [t('pkn.rpt.empty.bad')],
        focus: t('pkn.rpt.empty.focus'),
        good: [t('pkn.rpt.empty.good')],
        lead: t('pkn.rpt.empty.lead', { period: periodLabel }),
      };
    }
    const incomeDelta = compare ? rptPct(income, compare.summary.income) : null;
    const costDelta = compare ? rptPct(cost, compare.summary.expenses) : null;
    const deltas = compare ? current.categories.map((entry) => {
      const was = Number(compareByCategory.get(entry.category)?.amount || 0);
      return { diff: entry.amount - was, name: entry.category, pct: rptPct(entry.amount, was) };
    }) : [];
    const risers = deltas.filter((entry) => entry.diff > 0).sort((a, b) => b.diff - a.diff);
    const fallers = deltas.filter((entry) => entry.diff < 0).sort((a, b) => a.diff - b.diff);
    const topCategory = current.categories[0];
    const mover = risers[0] || fallers[0];
    const moverClause = mover ? ` ${t(mover.diff > 0 ? 'pkn.rpt.moverUp' : 'pkn.rpt.moverDown', { name: rptLower(mover.name), amount: fmt(Math.abs(mover.diff)), period: compareLabel })}` : '';
    const strengthClause = compare ? ` ${t(saved >= compareSaved ? 'pkn.rpt.strengthYes' : 'pkn.rpt.strengthNo', { period: compareLabel })}` : '';
    const rhythm = savingsRate >= 20 ? t('pkn.rpt.rhythm.healthy') : savingsRate >= 10 ? t('pkn.rpt.rhythm.fine') : t('pkn.rpt.rhythm.low');
    const lead = `${t(firstName ? 'pkn.rpt.leadNamed' : 'pkn.rpt.lead', { name: firstName, period: periodLabel, income: fmt(income), cost: fmt(cost), saved: fmt(saved), rate: rptDec(savingsRate) })}${strengthClause}${moverClause} ${rhythm}`;

    const good = [];
    if (incomeDelta > 0) good.push(t('pkn.rpt.good.incomeUp', { pct: rptPctStr(incomeDelta), year: compareEffective }));
    if (compareRate !== null && savingsRate > compareRate + 0.1) good.push(t('pkn.rpt.good.rateUp', { from: rptDec(compareRate), to: rptDec(savingsRate) }));
    if (costDelta < 0) good.push(t('pkn.rpt.good.costDown', { pct: rptPctStr(costDelta) }));
    if (fallers[0]) good.push(t('pkn.rpt.good.lessOn', { amount: fmt(-fallers[0].diff), name: rptLower(fallers[0].name) }));
    if (!good.length) good.push(savingsRate >= 20 ? t('pkn.rpt.good.rateHealthy', { rate: rptDec(savingsRate) }) : t('pkn.rpt.good.fixedStable'));

    const bad = [];
    if (costDelta > 0) bad.push(t('pkn.rpt.bad.costUp', { pct: rptPctStr(costDelta), year: compareEffective }));
    if (compareRate !== null && savingsRate < compareRate - 0.1) bad.push(t('pkn.rpt.bad.rateDown', { from: rptDec(compareRate), to: rptDec(savingsRate) }));
    if (risers[0]) bad.push(t('pkn.rpt.bad.mostUp', { name: risers[0].name, pct: rptPctStr(risers[0].pct), amount: fmt(risers[0].diff) }));
    if (incomeDelta < 0) bad.push(t('pkn.rpt.bad.incomeLower', { pct: rptPctStr(incomeDelta) }));
    if (!bad.length) bad.push(savingsRate < 10 ? t('pkn.rpt.bad.rateLow', { rate: rptDec(savingsRate) }) : t('pkn.rpt.bad.nothing'));

    const focus = risers[0]
      ? t('pkn.rpt.focus.watch', { name: rptLower(risers[0].name) })
      : topCategory
        ? t('pkn.rpt.focus.biggest', { name: topCategory.name })
        : t('pkn.rpt.focus.continue');

    return { bad: bad.slice(0, 3), focus, good: good.slice(0, 3), lead };
  }, [compare, compareEffective, compareLabel, compareRate, compareSaved, cost, current, firstName, hasData, income, periodLabel, saved, savingsRate]);

  const score = rptUseMemo(() => {
    if (!hasData) return 0;
    const incomeDelta = compare ? rptPct(income, compare.summary.income) : null;
    const costDelta = compare ? rptPct(cost, compare.summary.expenses) : null;
    let value = 55 + savingsRate * 1.5;
    if (incomeDelta !== null) value += incomeDelta > 0 ? 4 : incomeDelta < 0 ? -5 : 0;
    if (costDelta !== null) value += costDelta < 0 ? 6 : -Math.min(10, costDelta);
    if (savingsRate >= 20) value += 5;
    if (savingsRate < 8) value -= 6;
    return Math.max(20, Math.min(99, Math.round(value)));
  }, [compare, cost, hasData, income, savingsRate]);

  // ---------- Observasjoner: rekorder, trender, avvik ----------
  const observations = rptUseMemo(() => {
    if (!hasData) return [];
    const out = [];
    const incomes = chart.monthly.map((row) => Number(row.income || 0));
    const costs = chart.monthly.map((row) => Number(row.expenses || 0));
    if (monthly) {
      const upto = (list) => list.slice(0, month + 1).filter((value, index) => index === 0 || value > 0 || costs[index] > 0);
      if (month > 0 && incomes[month] > 0 && incomes[month] >= Math.max(...upto(incomes))) out.push({ icon: 'trendUp', text: t('pkn.rpt.obs.highestIncome', { amount: fmt(income) }), tone: 'record' });
      if (month > 0 && costs[month] > 0 && costs[month] <= Math.min(...upto(costs).filter(Boolean))) out.push({ icon: 'checkCircle', text: t('pkn.rpt.obs.lowestCost'), tone: 'good' });
      let streak = 0;
      for (let index = month; index >= 0; index -= 1) {
        if (incomes[index] > costs[index] && incomes[index] > 0) streak += 1;
        else break;
      }
      if (streak >= 3) out.push({ icon: 'checkCircle', text: t('pkn.rpt.obs.streak', { count: streak }), tone: 'good' });
      if (month >= 3) {
        const average = (costs[month - 1] + costs[month - 2] + costs[month - 3]) / 3;
        const deviation = average ? (costs[month] - average) / average * 100 : 0;
        if (Math.abs(deviation) >= 6) out.push({ icon: deviation > 0 ? 'alertCircle' : 'trendDown', text: t(deviation > 0 ? 'pkn.rpt.obs.deviationOver' : 'pkn.rpt.obs.deviationUnder', { pct: Math.abs(Math.round(deviation)) }), tone: deviation > 0 ? 'watch' : 'good' });
      }
      const biggest = compare ? current.categories.map((entry) => ({ name: entry.category, pct: rptPct(entry.amount, Number(compareByCategory.get(entry.category)?.amount || 0)) }))
        .filter((entry) => entry.pct !== null).sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct))[0] : null;
      if (biggest && Math.abs(biggest.pct) >= 15) out.push({ icon: biggest.pct > 0 ? 'trendUp' : 'trendDown', text: t('pkn.rpt.obs.standsOut', { name: biggest.name, pct: rptPctStr(biggest.pct), year: compareEffective }), tone: biggest.pct > 0 ? 'watch' : 'good' });
    } else {
      out.push({ icon: 'sparkle', text: t('pkn.rpt.obs.setAside', { amount: fmt(saved), year: selectedYear }), tone: saved >= 0 ? 'good' : 'watch' });
      if (compare) {
        const delta = rptPct(income, compare.summary.income);
        if (delta !== null) out.push({ icon: 'trendUp', text: t('pkn.rpt.obs.yearIncome', { pct: rptPctStr(delta), year: compareEffective }), tone: delta >= 0 ? 'record' : 'watch' });
      }
    }
    return out.slice(0, 4);
  }, [chart, compare, compareEffective, current, hasData, income, month, monthly, saved, selectedYear]);

  const outlook = rptUseMemo(() => {
    const monthlySaving = monthly ? saved : saved / 12;
    return { annual: Math.round(monthlySaving * 12) };
  }, [monthly, saved]);

  const askQuestions = rptUseMemo(() => {
    const mover = compare
      ? current.categories.map((entry) => ({ diff: Math.abs(entry.amount - Number(compareByCategory.get(entry.category)?.amount || 0)), name: entry.category })).sort((a, b) => b.diff - a.diff)[0]
      : current.categories[0];
    return [
      t('pkn.rpt.ask.why', { name: rptLower(mover?.name || mover?.category || t('pkn.rpt.ask.spendingFallback')) }),
      t('pkn.rpt.ask.saveMore'),
      compare ? t('pkn.rpt.ask.vsLastYear') : t('pkn.rpt.ask.rateEnough'),
      t('pkn.rpt.ask.whatIfContinue'),
    ];
  }, [compare, current]);

  const avatar = (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';
  const print = () => { toast(t('pkn.rpt.opening'), 'info'); setTimeout(() => window.print(), 350); };

  return <Card>
    <Sec icon="fileText" title={t('navgroup.reports')} sub={t('po.reportSub')}
      right={<div className="row gap-10" style={{ alignItems: 'center' }} data-pdf-exclude="">
        <Button variant="secondary" size="sm" icon="fileText" onClick={print}>{t('po.print')}</Button>{window.PoReportPdfButton && <window.PoReportPdfButton year={selectedYear} month={monthly ? month : null} />}
      </div>} />

    <div className="po-rpt-controls" data-pdf-exclude="">
      <div className="po-rpt-ctl">
        <span className="po-rpt-ctl-lbl">{t('vat.view')}</span>
        <Segmented value={mode} onChange={setMode} options={[{ label: t('pkn.rpt.monthly'), value: 'mnd' }, { label: t('pkn.rpt.yearly'), value: 'aar' }]} />
      </div>
      <div className="po-rpt-ctl">
        <span className="po-rpt-ctl-lbl">{t('sharedText.rep.export.year')}</span>
        <Segmented value={selectedYear} onChange={(value) => { setYear?.(value); if (compareYear === value) setCompareYear(0); }} options={years.map((item) => ({ label: String(item), value: item }))} />
      </div>
      <div className="po-rpt-ctl po-rpt-cmp-ctl">
        <span className="po-rpt-ctl-lbl" data-help-title={t('po.compareWith')} data-help={t('po.compareHelp')}>{t('po.compareWith')}</span>
        <Segmented value={compareEffective || 0} onChange={(value) => setCompareYear(value || 0)}
          options={[{ label: t('pkn.rpt.off'), value: 0 }, ...years.filter((item) => item !== selectedYear).map((item) => ({ label: String(item), value: item }))]} />
      </div>
    </div>

    {monthly && <div className="po-rpt-months" data-pdf-exclude="">
      <button className="po-rpt-mnav" onClick={() => setMonth((value) => Math.max(0, value - 1))} disabled={month === 0} aria-label={t('ap.prevMonth')}><Icon name="chevLeft" size={18} /></button>
      <div className="po-rpt-mlist">
        {RPT_M.map((label, index) => <button key={label} className={`po-rpt-mchip${index === month ? ' on' : ''}`} onClick={() => setMonth(index)}>{t('month.short.' + label)}</button>)}
      </div>
      <button className="po-rpt-mnav" onClick={() => setMonth((value) => Math.min(11, value + 1))} disabled={month === 11} aria-label={t('ap.nextMonth')}><Icon name="chevRight" size={18} /></button>
    </div>}

    <div className="po-rpt-head">
      <div>
        <div className="po-rpt-period">{periodLabel}</div>
        <div className="t-sm muted" style={{ fontWeight: 700 }}>{t(monthly ? 'pkn.rpt.monthReport' : 'pkn.rpt.yearReport')} · {t('pkn.rpt.basedOnBank')}{compare ? ` · ${t('pkn.rpt.comparedWith', { period: compareLabel })}` : ''}</div>
      </div>
      {ongoing && <span className="po-rpt-ongoing"><Icon name="alertCircle" size={15} />{t('po.ongoingPeriod')}</span>}
    </div>

    <div className="rpt-ai">
      <div className="rpt-ai-top">
        <span className="rpt-ai-ava"><img src={avatar} alt="SuperPlumma" /></span>
        <div className="rpt-ai-hd">
          <div className="rpt-ai-title">{t('po.plummaSummary')}</div>
          <div className="rpt-ai-sub">{t('pkn.rpt.writtenForYou', { period: periodLabel })}</div>
        </div>
        {hasData && <div className="rpt-score">
          <div className="rpt-score-grade" style={{ color: rptScoreColor(score) }}>{rptGrade(score)}</div>
          <div className="rpt-score-meta">
            <div className="rpt-score-num">{score}<span>/100</span></div>
            <div className="rpt-score-verdict">{rptVerdict(score)}</div>
          </div>
        </div>}
      </div>
      {hasData && <div className="rpt-score-meter"><div style={{ width: `${score}%`, background: rptScoreColor(score) }} /></div>}
      <p className="rpt-ai-lead">{aiSummary.lead}</p>
      {hasData && <React.Fragment>
        <div className="rpt-ai-cols">
          <div className="rpt-ai-block good">
            <div className="rpt-ai-bt"><Icon name="checkCircle" size={15} />{t('po.wentWell')}</div>
            <ul>{aiSummary.good.map((text, index) => <li key={index}><Icon name="check" size={14} />{text}</li>)}</ul>
          </div>
          <div className="rpt-ai-block bad">
            <div className="rpt-ai-bt"><Icon name="alertCircle" size={15} />{t('po.canImprove')}</div>
            <ul>{aiSummary.bad.map((text, index) => <li key={index}><Icon name="trendUp" size={14} />{text}</li>)}</ul>
          </div>
        </div>
      </React.Fragment>}
      <div className="rpt-ai-focus"><Icon name="sparkle" size={16} /><span><b>{t('po.focusNextMonth')}</b> {aiSummary.focus}</span></div>
      {onAsk && <div className="rpt-ask" data-pdf-exclude="">
        <div className="rpt-ask-head"><Icon name="sparkle" size={16} />{t('po.goDeeper')}</div>
        <div className="rpt-ask-chips">
          {askQuestions.map((question, index) => <button key={index} className="rpt-ask-chip" onClick={() => onAsk(question)}>{question}</button>)}
        </div>
        <button className="rpt-ask-bar" onClick={() => onAsk()}>
          <Icon name="sparkle" size={16} /><span className="rpt-ask-ph">{t('pkn.rpt.askPlaceholder')}</span><span className="rpt-ask-go"><Icon name="send" size={16} /></span>
        </button>
      </div>}
    </div>

    {observations.length > 0 && <div className="rpt-obs">
      <div className="rpt-obs-head"><Icon name="sparkle" size={16} />{t('po.plummaNoticed')}</div>
      <div className="rpt-obs-grid">
        {observations.map((observation, index) => <div key={index} className={`rpt-obs-item ${observation.tone}`}>
          <span className="rpt-obs-ico"><Icon name={observation.icon} size={16} /></span>
          <span className="rpt-obs-t">{observation.text}</span>
        </div>)}
      </div>
    </div>}

    <div className="grid-4 gap-12 po-rpt-kpis">
      <KPI tint="green" icon="arrowUpRight" label={t('home.legend.income')} value={fmt(income)} sub={sub(income, compare && compare.summary.income)} trend={compare ? income - compare.summary.income : null} />
      <KPI tint="peach" icon="receipt" label={t('home.donut.costs')} value={fmt(cost)} sub={sub(cost, compare && compare.summary.expenses)} trend={compare ? compare.summary.expenses - cost : null} />
      <KPI tint="plum" icon="wallet" label={t('pkn.rpt.netCashflow')} value={fmt(saved)} sub={sub(saved, compareSaved)} trend={compare ? saved - compareSaved : null}  help={t('helpTopics.cashFlow.body')}/>
      <KPI tint="white" icon="trendUp" label={t('po.savingsRate')} value={`${rptDec(savingsRate)} %`} sub={sub(savingsRate, compareRate, true)} trend={compareRate !== null ? savingsRate - compareRate : null}  help={t('helpTopics.savingsRate.body')}/>
    </div>

    <div className="po-rpt-chart">
      <div className="po-rpt-chart-head">
        <div className="t-h3" style={{ fontSize: 15 }}>{t('pkn.rpt.incomeCostPerMonth', { year: selectedYear })}</div>
        <div className="po-rpt-legend">
          <span><span className="po-rpt-sw" style={{ background: 'var(--success)' }} />{t('home.legend.income')}</span>
          <span><span className="po-rpt-sw" style={{ background: 'var(--plum-500)' }} />{t('home.legend.cost')}</span>
        </div>
      </div>
      <div style={{ aspectRatio: '3.7 / 1', overflow: 'hidden' }}><RptBars rows={chart.monthly} selected={month} monthly={monthly} /></div>
    </div>

    <div className={`po-rpt-table${compare ? ' cmp' : ''}`}>
      <div className="po-rpt-tr po-rpt-th">
        <span>{t('buy.category')}</span>
        <span className="num">{periodLabel}</span>
        {compare && <span className="num">{compareLabel}</span>}
        {compare && <span className="num">{t('clo.movement')}</span>}
      </div>
      {current.categories.length === 0 && <div className="po-rpt-tr"><span className="po-rpt-cat">{t('po.noImportedCosts')}</span><span className="num">{fmt(0)}</span>{compare && <span className="num muted">{fmt(0)}</span>}{compare && <span className="num">—</span>}</div>}
      {current.categories.map((entry, index) => {
        const was = compare ? Number(compareByCategory.get(entry.category)?.amount || 0) : null;
        const delta = was !== null ? rptPct(entry.amount, was) : null;
        return <div key={entry.category} className="po-rpt-tr po-rpt-tr-click" role="button" tabIndex={0}
          onClick={() => onOpenCategory?.(entry)}
          onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenCategory?.(entry); } }}
          title={t('pkn.for.seeTx', { name: entry.category })}>
          <span className="po-rpt-cat"><span className="po-rpt-dot" style={{ background: RPT_COLORS[index % RPT_COLORS.length] }} />{entry.category}</span>
          <span className="num">{fmt(entry.amount)}</span>
          {compare && <span className="num muted">{fmt(was)}</span>}
          {compare && <span className="num po-rpt-delta" style={{ color: delta > 0 ? 'var(--error)' : delta < 0 ? 'var(--success)' : 'var(--muted)' }}>
            {!!delta && <Icon name={delta > 0 ? 'trendUp' : 'trendDown'} size={13} />}{rptPctStr(delta)}
          </span>}
        </div>;
      })}
      <div className="po-rpt-tr po-rpt-total">
        <span>{t('po.totalCosts')}</span>
        <span className="num">{fmt(cost)}</span>
        {compare && <span className="num muted">{fmt(compare.summary.expenses)}</span>}
        {compare && <span className="num po-rpt-delta" style={{ color: rptPct(cost, compare.summary.expenses) > 0 ? 'var(--error)' : 'var(--success)' }}>{rptPctStr(rptPct(cost, compare.summary.expenses))}</span>}
      </div>
    </div>

    {hasData && <div className="rpt-outlook">
      <div className="rpt-outlook-title"><Icon name="trendUp" size={17} />{t('po.whatHappensNext')}</div>
      <div className="rpt-outlook-sub">{t('pkn.rpt.outlookSub', { period: periodLabel })}</div>
      <div className="rpt-outlook-grid">
        <div className="rpt-outlook-stat"><div className="rpt-outlook-l">{t('po.setAsideOver12')}</div><div className="rpt-outlook-v">{fmt(outlook.annual)}</div></div>
        <div className="rpt-outlook-stat"><div className="rpt-outlook-l">{t('po.averagePerMonth')}</div><div className="rpt-outlook-v">{fmt(Math.round(outlook.annual / 12))}</div></div>
        <div className="rpt-outlook-stat"><div className="rpt-outlook-l">{t('po.savingsRate')}</div><div className="rpt-outlook-v">{rptDec(savingsRate)}<span> %</span></div></div>
      </div>
      <p className="rpt-outlook-note">{t('pkn.rpt.outlookNoteA', { period: periodLabel })} <b>{fmt(outlook.annual)}</b> {t('pkn.rpt.outlookNoteB')}</p>
      {onAsk && <button className="rpt-outlook-cta" data-pdf-exclude="" onClick={() => onAsk(t('pkn.rpt.ask.whatIfContinue'))}><Icon name="sparkle" size={15} />{t('po.askWhatHappensNext')}</button>}
    </div>}

    <div className="t-xs muted" style={{ fontWeight: 600, marginTop: 14 }}>{t('pkn.rpt.footerNote')}</div>
  </Card>;
}

Object.assign(window, { PoReport });
